// ─────────────────────────────────────────────────────────────
// leads.dto.ts — TypeScript interfaces for the Leads CRM module
// ─────────────────────────────────────────────────────────────

export type LeadStage =
  | 'NEW'
  | 'CONTACTED'
  | 'QUOTED'
  | 'NEGOTIATING'
  | 'ADVANCE_PAID'
  | 'BOOKED'
  | 'LOST';

export type LeadSource =
  | 'whatsapp'
  | 'manual'
  | 'form'
  | 'referral'
  | 'call'
  | 'instagram'
  | 'google';

export type LeadEventType =
  | 'wedding'
  | 'pre_wedding'
  | 'engagement'
  | 'birthday'
  | 'corporate'
  | 'baby_shoot'
  | 'maternity'
  | 'other';

export type AIPriority = 'P1' | 'P2' | 'P3';

export type ActivityType =
  | 'lead_created'
  | 'stage_change'
  | 'note_added'
  | 'follow_up_sent'
  | 'ai_scored'
  | 'marked_lost'
  | 'whatsapp_sent';

// ─── Stage Transition Map ───────────────────────────────────
export const VALID_TRANSITIONS: Record<LeadStage, LeadStage[]> = {
  NEW: ['CONTACTED', 'LOST'],
  CONTACTED: ['QUOTED', 'NEGOTIATING', 'LOST'],
  QUOTED: ['NEGOTIATING', 'ADVANCE_PAID', 'LOST'],
  NEGOTIATING: ['ADVANCE_PAID', 'LOST'],
  ADVANCE_PAID: ['BOOKED', 'LOST'],
  BOOKED: ['LOST'],
  LOST: [],
};

export const STAGE_ORDER: LeadStage[] = [
  'NEW', 'CONTACTED', 'QUOTED', 'NEGOTIATING', 'ADVANCE_PAID', 'BOOKED', 'LOST'
];

export const STAGE_LABELS: Record<LeadStage, string> = {
  NEW: 'New Inquiry',
  CONTACTED: 'Contacted',
  QUOTED: 'Quoted',
  NEGOTIATING: 'Negotiating',
  ADVANCE_PAID: 'Advance Paid',
  BOOKED: 'Booked',
  LOST: 'Lost',
};

export const EVENT_TYPE_LABELS: Record<LeadEventType, string> = {
  wedding: 'Wedding',
  pre_wedding: 'Pre-Wedding',
  engagement: 'Engagement',
  birthday: 'Birthday',
  corporate: 'Corporate',
  baby_shoot: 'Baby Shoot',
  maternity: 'Maternity',
  other: 'Other',
};

export const SOURCE_LABELS: Record<LeadSource, string> = {
  whatsapp: 'WhatsApp',
  manual: 'Manual',
  form: 'Form',
  referral: 'Referral',
  call: 'Call',
  instagram: 'Instagram',
  google: 'Google',
};

export const EVENT_TYPES: { value: LeadEventType; label: string }[] = [
  { value: 'wedding', label: 'Wedding' },
  { value: 'pre_wedding', label: 'Pre-Wedding' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'birthday', label: 'Birthday' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'baby_shoot', label: 'Baby Shoot' },
  { value: 'maternity', label: 'Maternity' },
  { value: 'other', label: 'Other' },
];

export const SOURCES: { value: LeadSource; label: string }[] = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'manual', label: 'Manual' },
  { value: 'form', label: 'Form' },
  { value: 'referral', label: 'Referral' },
  { value: 'call', label: 'Call' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'google', label: 'Google' },
];

// ─── Lead Card (list response) ──────────────────────────────
export interface LeadCard {
  id: string;
  name: string;
  phone: string;
  country_code: string | null;
  email: string | null;
  event_type: LeadEventType;
  event_date: string | null;
  venue_city: string | null;
  budget_min: string | null;
  budget_max: string | null;
  stage: LeadStage;
  ai_score: number;
  ai_priority: AIPriority | null;
  source: LeadSource;
  last_activity_at: string;
  follow_up_count: number;
  created_at: string;
}

// ─── Lead Detail (single lead response) ────────────────────
export interface LeadActivity {
  id: string;
  activity_type: ActivityType;
  note: string | null;
  meta: Record<string, any> | null;
  from_stage?: LeadStage | null;
  to_stage?: LeadStage | null;
  created_at: string;
  created_by_name: string | null;
}

export interface LeadDetail extends LeadCard {
  assigned_to: string | null;
  guest_count: number | null;
  source_ref: string | null;
  quoted_amount: string | null;
  booked_amount: string | null;
  lost_reason: string | null;
  next_follow_up_at: string | null;
  follow_up_paused: boolean;
  notes: string | null;
  updated_at: string;
  activities: LeadActivity[];
}

// ─── Paginated Responses ────────────────────────────────────
export interface PaginatedLeads {
  count: number;
  next: string | null;
  previous: string | null;
  results: LeadCard[];
}

export interface PaginatedActivities {
  count: number;
  next: string | null;
  previous: string | null;
  results: LeadActivity[];
}

// ─── Stats ──────────────────────────────────────────────────
export interface LeadStats {
  total_leads: number;
  by_stage: Record<LeadStage, number>;
  by_priority: Record<AIPriority, number>;
  pipeline_value: string;
  stale_leads: number;
  conversion_rate: string;
}

// ─── WhatsApp Intake ────────────────────────────────────────
export type IntakeStatus = 'collecting' | 'complete' | 'converted';

export interface WhatsAppIntakeResponse {
  customer_number: string;
  phone: string;
  intake_status: IntakeStatus;
  intake_id: string | null;
  collected_data: Partial<{
    phone: string;
    country_code: string;
    name: string;
    event_type: LeadEventType;
    event_date: string;
    venue_city: string;
  }>;
  can_create_lead: boolean;
  active_lead_id: string | null;
  missing_fields: string[];
  event_type_choices: { value: string; label: string }[];
}

// ─── Create / Update Payloads ───────────────────────────────
export interface CreateLeadPayload {
  name: string;
  phone: string;
  country_code: string;
  event_type: LeadEventType;
  event_date?: string;
  venue_city?: string;
  email?: string;
  guest_count?: number;
  budget_min?: string;
  budget_max?: string;
  source?: LeadSource;
  source_ref?: string;
  notes?: string;
  ai_priority?: AIPriority;
}

export interface UpdateLeadPayload {
  name?: string;
  phone?: string;
  country_code?: string;
  email?: string;
  event_type?: LeadEventType;
  event_date?: string;
  venue_city?: string;
  guest_count?: number;
  budget_min?: string;
  budget_max?: string;
  source?: LeadSource;
  source_ref?: string;
  quoted_amount?: string;
  booked_amount?: string;
  lost_reason?: string;
  next_follow_up_at?: string;
  follow_up_paused?: boolean;
  notes?: string;
  ai_priority?: AIPriority;
}

export interface UpdateStagePayload {
  stage: LeadStage;
  lost_reason?: string;
}

export interface BulkStageUpdatePayload {
  lead_ids: string[];
  stage: LeadStage;
}

export interface BulkStageUpdateResponse {
  updated: number;
  failed: string[];
}

// ─── Follow-up ──────────────────────────────────────────────
export interface TriggerFollowUpResponse {
  message: string;
  job_id: string;
  scheduled_at: string;
  touch_number: number;
  estimated_wallet_charge_inr: string;
}

export interface PauseFollowUpResponse {
  message: string;
  cancelled_jobs: number;
}

// ─── Query Params ───────────────────────────────────────────
export interface ListLeadsParams {
  page?: number;
  page_size?: number;
  stage?: LeadStage;
  source?: LeadSource;
  ai_priority?: AIPriority;
  event_type?: LeadEventType;
  search?: string;
  date_from?: string;
  date_to?: string;
  ordering?: string;
}
